import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import Litepicker from '@/components/Base/Litepicker';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import Table from '@/components/Base/Table';
import Pagination from '@/components/Base/Pagination';
import { FormSelect } from '@/components/Base/Form';
import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

// ── Types ──────────────────────────────────────────────────────────────────

type OosCustomer = {
  createdAt: string;
  company: string | null;
  category: string | null;
  contact: string | null;
  state: string | null;
};

type OosOutlet = {
  q7_4: string;
  outlet_code: string | null;
  outlet_name: string | null;
  zone: string | null;
  distributor: string | null;
  chain: string | null;
  report_count: number;
  customers: OosCustomer[];
};

type OosFmGroup = {
  fm: string | null;
  email: string | null;
  total_report_count: number;
  outlets: OosOutlet[];
};

// Columns that appear on each outlet sub-row
type OutletColKey = 'outlet_code' | 'outlet_name' | 'zone' | 'distributor' | 'chain' | 'report_count';

const OUTLET_COLUMNS: { key: OutletColKey; label: string; minWidth?: string }[] = [
  { key: 'outlet_code',  label: 'Outlet Code',  minWidth: '120px' },
  { key: 'outlet_name',  label: 'Outlet Name',  minWidth: '180px' },
  { key: 'zone',         label: 'Zone',         minWidth: '110px' },
  { key: 'distributor',  label: 'Distributor',  minWidth: '140px' },
  { key: 'chain',        label: 'Chain',        minWidth: '120px' },
  { key: 'report_count', label: 'Reports',      minWidth: '90px'  },
];

// All sortable/filterable keys on an outlet row
type SortableKey = OutletColKey;

const PAGE_SIZE_OPTIONS = [50, 100, 500, 1000] as const;

function outletCellValue(outlet: OosOutlet, key: OutletColKey): string {
  const v = outlet[key];
  if (v === null || v === undefined) return '';
  return String(v);
}

// ── Component ──────────────────────────────────────────────────────────────

function todayMYT() {
  return dayjs().tz ? dayjs().tz('Asia/Kuala_Lumpur').format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
}

function todayISORange() {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    startISO: dayjs(today).startOf('day').toISOString(),
    endISO:   dayjs(today).endOf('day').toISOString(),
  };
}

export default function OosEmailReport() {
  const todayStr = todayMYT();
  const [dateRange, setDateRange] = useState(`${todayStr} - ${todayStr}`);
  const [confirmedDateRangeISO, setConfirmedDateRangeISO] = useState<{
    startISO: string;
    endISO: string;
  } | null>(todayISORange());

  const [groups, setGroups] = useState<OosFmGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  // Which FM groups are collapsed (all expanded by default)
  const [collapsedFms, setCollapsedFms] = useState<Set<string>>(new Set());

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);

  const [sortColumn, setSortColumn] = useState<SortableKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [columnFilters, setColumnFilters] = useState<Partial<Record<SortableKey, string>>>({});

  const [openFilterKey, setOpenFilterKey] = useState<SortableKey | null>(null);
  const [filterPanelPos, setFilterPanelPos] = useState<{ top: number; left: number } | null>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  // Per-FM email sending state: fmKey → 'sending' | 'sent' | 'error'
  const [emailState, setEmailState] = useState<Record<string, 'sending' | 'sent' | 'error'>>({});

  // ── Filter popover close listeners ──
  useEffect(() => {
    if (!openFilterKey) return;
    const onPointerDown = (e: PointerEvent) => {
      if (filterPanelRef.current?.contains(e.target as Node)) return;
      if ((e.target as HTMLElement).closest('[data-column-filter-btn]')) return;
      setOpenFilterKey(null);
      setFilterPanelPos(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpenFilterKey(null); setFilterPanelPos(null); }
    };
    const onScroll = () => { setOpenFilterKey(null); setFilterPanelPos(null); };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [openFilterKey]);

  const dateRangeISO = useMemo(() => {
    const raw = (dateRange || '').trim();
    if (!raw) return null;
    const [startRaw, endRaw] = raw.split(' - ');
    const startStr = (startRaw || '').trim();
    const endStr = ((endRaw || '') || startStr).trim();
    if (!startStr) return null;
    const start = dayjs(startStr);
    const end = dayjs(endStr);
    if (!start.isValid() || !end.isValid()) return null;
    return {
      startISO: start.startOf('day').toISOString(),
      endISO: end.endOf('day').toISOString(),
    };
  }, [dateRange]);

  const handleSearch = () => {
    if (!dateRangeISO) return;
    setConfirmedDateRangeISO({ ...dateRangeISO });
  };

  useEffect(() => {
    if (!confirmedDateRangeISO) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setCurrentPage(1);
    setSortColumn(null);
    setSortDir('asc');
    setColumnFilters({});
    setEmailState({});
    setCollapsedFms(new Set());
    setOpenFilterKey(null);
    setFilterPanelPos(null);

    const origin = getApiOrigin();
    const params = new URLSearchParams({
      startDate: confirmedDateRangeISO.startISO,
      endDate: confirmedDateRangeISO.endISO,
    });

    fetch(`${origin}/api/v1/admin/oos-report?${params.toString()}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json: { ok: boolean; data: OosFmGroup[] }) => {
        if (!cancelled) { setGroups(json.data ?? []); setFetched(true); }
      })
      .catch((err: unknown) => {
        if (!cancelled) { setError(err instanceof Error ? err.message : 'Failed to load data'); setFetched(true); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [confirmedDateRangeISO]);

  // ── Sort / filter helpers ──
  const handleSortClick = (key: SortableKey) => {
    if (sortColumn === key) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortColumn(null); setSortDir('asc'); }
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (key: SortableKey, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Apply filters + sort to the outlets within every group, drop empty groups
  const filteredGroups = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v?.trim());

    return groups
      .map((g) => {
        let outlets = g.outlets;

        if (activeFilters.length > 0) {
          outlets = outlets.filter((o) =>
            activeFilters.every(([key, value]) => {
              const v = outletCellValue(o, key as OutletColKey);
              return v.toLowerCase().includes((value as string).toLowerCase());
            })
          );
        }

        if (sortColumn) {
          outlets = [...outlets].sort((a, b) => {
            if (sortColumn === 'report_count') {
              const cmp = a.report_count - b.report_count;
              return sortDir === 'asc' ? cmp : -cmp;
            }
            const va = outletCellValue(a, sortColumn);
            const vb = outletCellValue(b, sortColumn);
            const cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
            return sortDir === 'asc' ? cmp : -cmp;
          });
        }

        return { ...g, outlets, total_report_count: outlets.reduce((s, o) => s + o.report_count, 0) };
      })
      .filter((g) => g.outlets.length > 0);
  }, [groups, columnFilters, sortColumn, sortDir]);

  // Total outlet rows across all groups (for pagination)
  const totalOutlets = useMemo(
    () => filteredGroups.reduce((s, g) => s + g.outlets.length, 0),
    [filteredGroups]
  );

  const totalPages = useMemo(
    () => (totalOutlets === 0 ? 1 : Math.ceil(totalOutlets / pageSize)),
    [totalOutlets, pageSize]
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const startIndex = useMemo(() => (currentPage - 1) * pageSize, [currentPage, pageSize]);
  const endIndex   = useMemo(() => Math.min(startIndex + pageSize, totalOutlets), [startIndex, pageSize, totalOutlets]);

  // Slice the flat outlet list across groups for the current page,
  // then reconstruct the FM groups that fall within this page window
  const pagedGroups = useMemo(() => {
    const result: OosFmGroup[] = [];
    let outletIdx = 0;

    for (const g of filteredGroups) {
      const groupStart = outletIdx;
      const groupEnd = outletIdx + g.outlets.length;

      // overlap with [startIndex, endIndex)
      if (groupEnd > startIndex && groupStart < endIndex) {
        const sliceFrom = Math.max(0, startIndex - groupStart);
        const sliceTo   = Math.min(g.outlets.length, endIndex - groupStart);
        result.push({
          ...g,
          outlets: g.outlets.slice(sliceFrom, sliceTo),
        });
      }
      outletIdx = groupEnd;
    }
    return result;
  }, [filteredGroups, startIndex, endIndex]);

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const fmKey = (g: OosFmGroup) => g.fm ?? '__unknown__';

  const toggleCollapse = (key: string) => {
    setCollapsedFms((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // ── Send email ──
  const handleSendEmail = async (g: OosFmGroup, outletsToSend: OosOutlet[]) => {
    if (!g.email) {
      alert(`No email address found for Field Manager "${g.fm ?? 'Unknown'}".`);
      return;
    }

    const key = fmKey(g);
    setEmailState((prev) => ({ ...prev, [key]: 'sending' }));

    try {
      const origin = getApiOrigin();
      const res = await fetch(`${origin}/api/v1/admin/oos-report/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          fm: g.fm ?? '',
          toEmail: g.email,
          dateRange: dateRange || 'N/A',
          outlets: outletsToSend.map((o) => ({
            outlet_name: o.outlet_name,
            outlet_code: o.outlet_code,
            zone: o.zone,
            distributor: o.distributor,
            chain: o.chain,
            customers: o.customers,
          })),
        }),
      });

      const json = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !json.ok) throw new Error(json.error ?? `HTTP ${res.status}`);

      setEmailState((prev) => ({ ...prev, [key]: 'sent' }));
      alert(`Email sent successfully to ${g.email}.`);
    } catch (err) {
      setEmailState((prev) => ({ ...prev, [key]: 'error' }));
      alert(`Failed to send email: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const totalCols = OUTLET_COLUMNS.length;

  return (
    <div className="p-6">
      {/* ── Toolbar ── */}
      <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative inline-flex">
            <Lucide
              icon="Calendar"
              className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
            />
            <Litepicker
              value={dateRange}
              onChange={(e) => setDateRange((e.target?.value || '').trim())}
              options={{
                autoApply: true,
                singleMode: false,
                numberOfColumns: 2,
                numberOfMonths: 2,
                showWeekNumbers: true,
                dropdowns: { minYear: 2024, maxYear: null, months: true, years: true },
                format: 'YYYY-MM-DD',
              }}
              className="w-[270px] pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-sm"
            />
          </div>

          <Button
            variant="primary"
            onClick={handleSearch}
            disabled={!dateRangeISO || loading}
            className="w-full sm:w-auto"
          >
            {loading ? (
              <><Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />Loading…</>
            ) : (
              <><Lucide icon="Search" className="stroke-[1.3] w-4 h-4 mr-2" />Search</>
            )}
          </Button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
          {error}
        </div>
      )}

      {/* ── Table ── */}
      {fetched && !error && (
        <div className="box rounded-xl shadow-sm border border-slate-200/60 dark:border-darkmode-400 overflow-hidden">
          <div className="overflow-x-auto w-full">
            <Table hover className="text-sm whitespace-nowrap min-w-max">

              {/* Column headers */}
              <Table.Thead className="bg-green-500/35 dark:bg-darkmode-700 text-xs uppercase tracking-wide">
                <Table.Tr>
                  {OUTLET_COLUMNS.map((col) => {
                    const isActive = sortColumn === col.key;
                    const hasFilter = Boolean((columnFilters[col.key] ?? '').trim());
                    return (
                      <Table.Th
                        key={col.key}
                        className="border-r border-slate-300 dark:border-darkmode-400 py-2 px-2 align-middle"
                        style={{ minWidth: col.minWidth }}
                      >
                        <div className="flex items-center gap-1 min-w-0">
                          <button
                            type="button"
                            onClick={() => handleSortClick(col.key)}
                            className="flex items-center gap-0.5 min-w-0 flex-1 font-semibold text-[11px] text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white text-left group"
                          >
                            <span className="truncate">{col.label}</span>
                            <span className="flex-shrink-0">
                              {isActive && sortDir === 'asc'  && <Lucide icon="ArrowUp"        className="w-3 h-3 stroke-[2] text-primary" />}
                              {isActive && sortDir === 'desc' && <Lucide icon="ArrowDown"      className="w-3 h-3 stroke-[2] text-primary" />}
                              {!isActive                      && <Lucide icon="ChevronsUpDown" className="w-3 h-3 stroke-[1.5] opacity-30 group-hover:opacity-60" />}
                            </span>
                          </button>
                          <button
                            type="button"
                            data-column-filter-btn
                            title="Filter column"
                            className={[
                              'p-0.5 rounded flex-shrink-0 hover:bg-slate-200/80 dark:hover:bg-darkmode-500',
                              hasFilter ? 'text-primary' : 'text-slate-400 dark:text-slate-500',
                            ].join(' ')}
                            onClick={(e) => {
                              e.stopPropagation();
                              const r = e.currentTarget.getBoundingClientRect();
                              const panelW = 176;
                              let left = r.left;
                              if (left + panelW > window.innerWidth - 8) left = window.innerWidth - panelW - 8;
                              if (openFilterKey === col.key) { setOpenFilterKey(null); setFilterPanelPos(null); }
                              else { setOpenFilterKey(col.key); setFilterPanelPos({ top: r.bottom + 4, left: Math.max(8, left) }); }
                            }}
                          >
                            <Lucide icon="ListFilter" className="w-3.5 h-3.5 stroke-[1.5]" />
                          </button>
                        </div>
                      </Table.Th>
                    );
                  })}
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {pagedGroups.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={totalCols} className="py-10 text-center text-slate-400 dark:text-slate-500">
                      No OOS records found for the selected date range.
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  pagedGroups.map((g) => {
                    const key = fmKey(g);
                    const isCollapsed = collapsedFms.has(key);
                    const state = emailState[key];
                    // All outlets for this FM (across all pages) for the send-email payload
                    const allFmOutlets = filteredGroups.find((fg) => fmKey(fg) === key)?.outlets ?? g.outlets;

                    return (
                      <>
                        {/* ── FM group header row ── */}
                        <Table.Tr key={`fm-${key}`}>
                          <Table.Td
                            colSpan={totalCols}
                            className="bg-slate-100 dark:bg-darkmode-700 border-b border-slate-200 dark:border-darkmode-400 px-0 py-0"
                          >
                            <div className="flex items-center gap-3 px-3 py-2">
                              {/* toggle + FM name + meta + send button */}
                              <div className="flex items-center gap-2 min-w-0">
                                <button
                                  type="button"
                                  onClick={() => toggleCollapse(key)}
                                  className="flex-shrink-0 p-0.5 rounded hover:bg-slate-200 dark:hover:bg-darkmode-500 text-slate-500"
                                >
                                  <Lucide
                                    icon={isCollapsed ? 'ChevronRight' : 'ChevronDown'}
                                    className="w-4 h-4 stroke-[2]"
                                  />
                                </button>
                                <span className="font-semibold text-[12px] text-slate-700 dark:text-slate-200 truncate">
                                  {g.fm ?? <span className="italic text-slate-400">Unknown FM</span>}
                                </span>
                                <span className="flex-shrink-0 ml-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[11px] font-semibold">
                                  {g.outlets.length} outlet{g.outlets.length !== 1 ? 's' : ''}
                                </span>
                                <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[11px] font-semibold">
                                  {g.total_report_count} report{g.total_report_count !== 1 ? 's' : ''}
                                </span>
                              </div>

                              {/* Send email button — sits right after the reports badge */}
                              <button
                                type="button"
                                title={
                                  !g.email
                                    ? 'No email address — cannot send'
                                    : state === 'sent'
                                    ? 'Email sent'
                                    : state === 'error'
                                    ? 'Error — click to retry'
                                    : `Send OOS report to ${g.email}`
                                }
                                disabled={state === 'sending' || !g.email}
                                onClick={() => handleSendEmail(g, allFmOutlets)}
                                className={[
                                  'flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-colors',
                                  !g.email
                                    ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                                    : state === 'sent'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : state === 'error'
                                    ? 'bg-danger/10 text-danger hover:bg-danger/20'
                                    : state === 'sending'
                                    ? 'text-slate-400 cursor-wait'
                                    : 'bg-white dark:bg-darkmode-500 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-darkmode-400 hover:bg-slate-50 dark:hover:bg-darkmode-400',
                                ].join(' ')}
                              >
                                {state === 'sending' ? (
                                  <><Lucide icon="Loader"      className="w-3.5 h-3.5 animate-spin" />Sending…</>
                                ) : state === 'sent' ? (
                                  <><Lucide icon="CheckCheck"  className="w-3.5 h-3.5" />Sent</>
                                ) : state === 'error' ? (
                                  <><Lucide icon="AlertCircle" className="w-3.5 h-3.5" />Retry</>
                                ) : (
                                  <><Lucide icon="Send"        className="w-3.5 h-3.5" />Send Email</>
                                )}
                              </button>
                            </div>
                          </Table.Td>
                        </Table.Tr>

                        {/* ── Outlet sub-rows (hidden when collapsed) ── */}
                        {!isCollapsed &&
                          g.outlets.map((outlet) => (
                            <Table.Tr
                              key={`${key}-${outlet.q7_4}`}
                              className="[&_td]:border-b [&_td]:border-slate-100 dark:[&_td]:border-darkmode-400"
                            >
                              {OUTLET_COLUMNS.map((col) => (
                                <Table.Td
                                  key={col.key}
                                  className="border-r border-slate-600 dark:border-darkmode-400 py-2.5 pl-8 pr-4 text-[11px] text-slate-700 dark:text-slate-300"
                                >
                                  {col.key === 'report_count' ? (
                                    <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-semibold text-[11px]">
                                      {outlet.report_count}
                                    </span>
                                  ) : (
                                    outletCellValue(outlet, col.key)
                                  )}
                                </Table.Td>
                              ))}
                            </Table.Tr>
                          ))}
                      </>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </div>

          {/* ── Pagination footer ── */}
          <div className="flex flex-col-reverse flex-wrap items-center justify-between gap-y-2 px-5 py-4 border-t border-slate-100 dark:border-darkmode-400 sm:flex-row sm:gap-x-3">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {totalOutlets > 0 ? (
                <>Showing {startIndex + 1}–{endIndex} of {totalOutlets} outlets</>
              ) : (
                'No records'
              )}
            </div>

            <Pagination className="flex-1 w-full sm:w-auto">
              <Pagination.Link onClick={(e) => { e.preventDefault(); if (currentPage > 1) setCurrentPage(1); }} className={currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}>
                <Lucide icon="ChevronsLeft"  className="w-3 h-3" />
              </Pagination.Link>
              <Pagination.Link onClick={(e) => { e.preventDefault(); if (currentPage > 1) setCurrentPage((p) => p - 1); }} className={currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}>
                <Lucide icon="ChevronLeft"   className="w-3 h-3" />
              </Pagination.Link>

              {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                let pageNum: number;
                if (totalPages <= 5)                    pageNum = i + 1;
                else if (currentPage <= 3)              pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else                                    pageNum = currentPage - 2 + i;
                return (
                  <Pagination.Link key={pageNum} active={currentPage === pageNum} onClick={(e) => { e.preventDefault(); setCurrentPage(pageNum); }} className="cursor-pointer">
                    {pageNum}
                  </Pagination.Link>
                );
              })}

              <Pagination.Link onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) setCurrentPage((p) => p + 1); }} className={currentPage >= totalPages ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}>
                <Lucide icon="ChevronRight"  className="w-3 h-3" />
              </Pagination.Link>
              <Pagination.Link onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) setCurrentPage(totalPages); }} className={currentPage >= totalPages ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}>
                <Lucide icon="ChevronsRight" className="w-3 h-3" />
              </Pagination.Link>
            </Pagination>

            <FormSelect className="w-20 rounded-[0.5rem]" value={pageSize} onChange={handlePageSizeChange}>
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </FormSelect>
          </div>
        </div>
      )}

      {/* ── Initial empty state ── */}
      {!fetched && !loading && !error && (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400 dark:text-slate-500">
          <Lucide icon="CalendarSearch" className="w-10 h-10 mb-3 stroke-[1.2]" />
          <p className="text-sm">Select a date range above to load OOS data.</p>
        </div>
      )}

      {/* ── Filter pop-up panel (portal) ── */}
      {openFilterKey && filterPanelPos && createPortal(
        <div
          ref={filterPanelRef}
          className="fixed z-[300] w-44 rounded-lg border border-slate-200 bg-white p-2.5 shadow-[0px_3px_10px_#00000017] dark:border-darkmode-400 dark:bg-darkmode-600"
          style={{ top: filterPanelPos.top, left: filterPanelPos.left }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {OUTLET_COLUMNS.find((c) => c.key === openFilterKey)?.label ?? 'Filter'}
          </div>
          <input
            type="text"
            value={columnFilters[openFilterKey] ?? ''}
            onChange={(e) => handleFilterChange(openFilterKey, e.target.value)}
            placeholder="Contains…"
            className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-darkmode-400 dark:bg-darkmode-700 dark:text-slate-200"
            autoFocus
          />
          {(columnFilters[openFilterKey] ?? '').trim() ? (
            <button type="button" className="mt-2 text-xs text-primary hover:underline" onClick={() => handleFilterChange(openFilterKey, '')}>
              Clear filter
            </button>
          ) : null}
        </div>,
        document.body
      )}
    </div>
  );
}
